import { useState } from 'react'
import toast from 'react-hot-toast'
import { Shield, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, Button, Label } from '@jira-clone/ui'
import {
  usePermissionTemplates,
  useUpdatePermissionTemplate,
  useCreatePermissionTemplate,
  useApplyPermissionTemplate,
  useDeletePermissionTemplate,
  STORABLE_ROLES,
  type PermissionTemplate,
} from '../shared/hooks/usePermissionTemplates'
import { useDepartments } from '../shared/hooks/useDepartments'
import { getApiErrorMessage } from '../shared/utils/apiError'
import { ConfirmDialog } from '../shared/components/ui/ConfirmDialog'
import {
  PermissionToggleGrid,
  togglePermissionEntry,
  toggleAllModuleActions,
  type PermissionEntry,
} from '../shared/components/PermissionToggleGrid'

// Lets a superadmin edit the default module/action bundle a new employee
// gets, per department + role tier — what used to be six bundles hardcoded
// in seed-goodday-users.ts. Saving a template here only changes what a
// *future* hire in that department starts with; it never automatically
// touches an existing employee's own permissions. To push a template out to
// employees already in that department + role, use the explicit "Apply to
// existing users" action below — it skips anyone whose access has been
// individually customized via People > Manage Access.
export function PermissionTemplatesPanel() {
  const { data: templates = [], isLoading } = usePermissionTemplates()
  const { data: departments = [] } = useDepartments()
  const updateTemplate = useUpdatePermissionTemplate()
  const createTemplate = useCreatePermissionTemplate()
  const applyTemplate = useApplyPermissionTemplate()
  const deleteTemplate = useDeletePermissionTemplate()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Record<string, PermissionEntry[]>>({})
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [applyingId, setApplyingId] = useState<string | null>(null)
  const [confirmApplyTpl, setConfirmApplyTpl] = useState<PermissionTemplate | null>(null)
  const [confirmDeleteTpl, setConfirmDeleteTpl] = useState<PermissionTemplate | null>(null)

  const [creating, setCreating] = useState(false)
  const [newDepartmentId, setNewDepartmentId] = useState('')
  const [newRole, setNewRole] = useState<(typeof STORABLE_ROLES)[number]>('MEMBER')
  const [copyFromId, setCopyFromId] = useState('')

  const startEditing = (tpl: PermissionTemplate) => {
    setExpandedId((prev) => (prev === tpl.id ? null : tpl.id))
    setDraft((prev) => ({ ...prev, [tpl.id]: prev[tpl.id] || tpl.permissions }))
  }

  const toggle = (tpl: PermissionTemplate, module: string, action: string) => {
    setDraft((prev) => ({
      ...prev,
      [tpl.id]: togglePermissionEntry(prev[tpl.id] || tpl.permissions, module, action),
    }))
  }

  const toggleAllForTemplate = (tpl: PermissionTemplate, module: string, enable: boolean) => {
    setDraft((prev) => ({
      ...prev,
      [tpl.id]: toggleAllModuleActions(prev[tpl.id] || tpl.permissions, module, enable),
    }))
  }

  const save = async (tpl: PermissionTemplate) => {
    try {
      await updateTemplate.mutateAsync({ id: tpl.id, permissions: draft[tpl.id] || tpl.permissions })
      toast.success(`Default permissions for ${tpl.departmentName} updated`)
      setExpandedId(null)
    } catch {
      toast.error('Failed to update template')
    }
  }

  const resetCreateForm = () => {
    setCreating(false)
    setNewDepartmentId('')
    setNewRole('MEMBER')
    setCopyFromId('')
  }

  const handleCreate = async () => {
    if (!newDepartmentId) {
      toast.error('Pick a department first.')
      return
    }
    const copySource = copyFromId ? templates.find((t) => t.id === copyFromId) : undefined
    try {
      const result = await createTemplate.mutateAsync({
        departmentId: newDepartmentId,
        role: newRole,
        permissions: copySource?.permissions,
      })
      const applied = result?.appliedToExistingUsers ?? 0
      const skipped = result?.skippedCustomizedUsers ?? 0
      toast.success(
        applied > 0
          ? `Template created and applied to ${applied} existing employee${applied === 1 ? '' : 's'}${skipped > 0 ? ` (${skipped} with customized access left as-is)` : ''}.`
          : 'Template created.'
      )
      resetCreateForm()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'create this template'))
    }
  }

  const confirmApply = async () => {
    const tpl = confirmApplyTpl
    if (!tpl) return
    setApplyingId(tpl.id)
    try {
      const result = await applyTemplate.mutateAsync(tpl.id)
      toast.success(result.message)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'apply this template'))
    } finally {
      setApplyingId(null)
      setConfirmApplyTpl(null)
    }
  }

  const confirmDelete = async () => {
    const tpl = confirmDeleteTpl
    if (!tpl) return
    setDeletingId(tpl.id)
    try {
      await deleteTemplate.mutateAsync(tpl.id)
      toast.success('Template deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this template'))
    } finally {
      setDeletingId(null)
      setConfirmDeleteTpl(null)
    }
  }

  const selectClass =
    'w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring'

  const createForm = creating ? (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label htmlFor="new-tpl-dept">Department</Label>
            <select
              id="new-tpl-dept"
              value={newDepartmentId}
              onChange={(e) => setNewDepartmentId(e.target.value)}
              className={selectClass}
            >
              <option value="">Select a department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-tpl-role">Role</Label>
            <select
              id="new-tpl-role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as (typeof STORABLE_ROLES)[number])}
              className={selectClass}
            >
              {STORABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-tpl-copy">Copy permissions from (optional)</Label>
            <select
              id="new-tpl-copy"
              value={copyFromId}
              onChange={(e) => setCopyFromId(e.target.value)}
              className={selectClass}
            >
              <option value="">Start empty</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.departmentName} / {t.role}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={resetCreateForm}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleCreate} disabled={createTemplate.isPending}>
            Create Template
          </Button>
        </div>
      </CardContent>
    </Card>
  ) : null

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading default permission templates...</p>
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          The default module access a new employee gets when added to a department. Saving a template
          only affects future hires — use "Apply to existing users" on a template to push it out to
          employees already in that department + role (anyone individually customized is skipped).
        </p>
        {!creating && (
          <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5" /> New Template
          </Button>
        )}
      </div>
      {createForm}
      {templates.length === 0 && !creating && (
        <p className="text-sm text-muted-foreground">No permission templates configured yet.</p>
      )}
      {templates.map((tpl) => {
        const isOpen = expandedId === tpl.id
        const permissions = draft[tpl.id] || tpl.permissions
        return (
          <Card key={tpl.id}>
            <CardHeader
              className="cursor-pointer flex flex-row items-center justify-between py-3"
              onClick={() => startEditing(tpl)}
            >
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#6E11E3]" />
                {tpl.departmentName}
                <span className="text-xs font-normal text-muted-foreground">({tpl.role})</span>
              </CardTitle>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={deletingId === tpl.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    setConfirmDeleteTpl(tpl)
                  }}
                  title="Delete template"
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
                {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            {isOpen && (
              <CardContent className="pt-0 space-y-3">
                <PermissionToggleGrid
                  permissions={permissions}
                  onToggle={(m, a) => toggle(tpl, m, a)}
                  onToggleAll={(m, enable) => toggleAllForTemplate(tpl, m, enable)}
                />
                <div className="flex justify-between gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmApplyTpl(tpl)}
                    disabled={applyingId === tpl.id}
                    title="Push this template's current permissions out to employees already in this department + role, skipping anyone with customized access"
                  >
                    {applyingId === tpl.id ? 'Applying...' : 'Apply to existing users'}
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setExpandedId(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => save(tpl)} disabled={updateTemplate.isPending}>
                      Save Default
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        )
      })}

      <ConfirmDialog
        open={!!confirmApplyTpl}
        title="Apply template to existing users"
        message={
          confirmApplyTpl
            ? `Apply the current ${confirmApplyTpl.departmentName} / ${confirmApplyTpl.role} template to everyone already in that department + role? Anyone with individually customized access via Manage Access will be skipped.`
            : ''
        }
        confirmLabel="Apply"
        loading={!!confirmApplyTpl && applyingId === confirmApplyTpl.id}
        onCancel={() => setConfirmApplyTpl(null)}
        onConfirm={confirmApply}
      />

      <ConfirmDialog
        open={!!confirmDeleteTpl}
        title="Delete template"
        message={
          confirmDeleteTpl
            ? `Delete the ${confirmDeleteTpl.departmentName} / ${confirmDeleteTpl.role} template? This only removes the default for future hires in this department + role — it won't change any current employee's access.`
            : ''
        }
        confirmLabel="Delete"
        danger
        loading={!!confirmDeleteTpl && deletingId === confirmDeleteTpl.id}
        onCancel={() => setConfirmDeleteTpl(null)}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
